// /slot/:slug/:date/:hhmm — anonymised "who's going" view.
//
// Reuses /api/v1/airfields/:slug/slots for the single-day window so we
// don't pay the cost of a per-slot endpoint just for one screen. Skill
// and aircraft breakdowns are placeholders until RCF-018 (garage) and
// RCF-019 (profile) populate the source data.

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Section, Cell, List, Caption, Spinner, Banner, Title,
} from "@telegram-apps/telegram-ui";
import { api } from "../lib/api.js";
import { usePageBack } from "../lib/telegram.js";

const RU_WEEKDAYS = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const RU_MONTHS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

const CLASS_LABELS = {
  F3A: { emoji: "✈️", title: "Самолёты", short: "F3A" },
  F9U: { emoji: "🚁", title: "Дроны / коптеры", short: "F9U" },
};

export default function SlotDetail() {
  const { slug, date, hhmm } = useParams();
  const navigate = useNavigate();
  usePageBack("/flights");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api(`/api/v1/airfields/${slug}/slots?from=${date}&to=${date}`)
      .then((r) => {
        const day = (r.days || []).find((d) => d.date === date);
        if (!day) { setError("Не нашли расписание на эту дату."); return; }
        const slotByClass = {};
        let totalBooked = 0;
        let totalCapacity = 0;
        let startAtIso = null, endAtIso = null;
        for (const cls of day.classes || []) {
          const bucket = (cls.buckets || []).find((b) => b.hhmm === hhmm);
          if (!bucket) continue;
          slotByClass[cls.class] = {
            booked: bucket.booked,
            max: bucket.max_concurrent,
            available: bucket.available,
          };
          totalBooked += bucket.booked || 0;
          if (typeof bucket.max_concurrent === "number") totalCapacity += bucket.max_concurrent;
          startAtIso = startAtIso || bucket.startAtIso;
          endAtIso = endAtIso || bucket.endAtIso;
        }
        if (!startAtIso) {
          setError("На эту дату нет такого слота. Возможно, расписание изменилось.");
          return;
        }
        setData({
          day, hhmm, startAtIso, endAtIso,
          totalBooked, totalCapacity,
          slotByClass,
        });
      })
      .catch((e) => setError(`Не удалось загрузить слот: ${e.message}`));
  }, [slug, date, hhmm]);

  if (error) {
    return (
      <List>
        <Banner type="section" header="Ошибка">{error}</Banner>
        <Section><Cell onClick={() => navigate(-1)}>← Назад</Cell></Section>
      </List>
    );
  }
  if (!data) {
    return (
      <List>
        <Section><Cell before={<Spinner size="m" />}>Загружаем слот…</Cell></Section>
      </List>
    );
  }

  const spotsLeft = data.totalCapacity > 0
    ? Math.max(0, data.totalCapacity - data.totalBooked)
    : null;

  return (
    <List>
      <Section header="Слот">
        <Cell subtitle={`${formatDateRu(data.day.date)} · ${data.hhmm}–${endHhmm(data.endAtIso)}`} multiline>
          <Title level="3">{data.day.airspace_status === "closed" ? "🚫 Поле закрыто" : "Кто записан"}</Title>
        </Cell>
      </Section>

      <Section header="Аэродром на этот день">
        <Cell subtitle="Воздушное пространство">{airspaceLine(data.day)}</Cell>
        <Cell subtitle="Погода">{weatherLine(data.day)}</Cell>
      </Section>

      <Section header="Кто записан">
        <Cell subtitle="Без имён — только агрегаты, как и задумано." multiline>
          <Title level="2">
            {data.totalBooked} {pluralRu(data.totalBooked, "пилот", "пилота", "пилотов")}
          </Title>
        </Cell>
        {spotsLeft !== null && (
          <Cell subtitle="Свободных мест в слоте">{spotsLeft}</Cell>
        )}
      </Section>

      <Section header="По типу модели">
        {Object.entries(data.slotByClass).map(([code, info]) => {
          const meta = CLASS_LABELS[code] || { emoji: "·", title: code, short: code };
          const cap = typeof info.max === "number"
            ? `${info.booked}/${info.max}`
            : `${info.booked}+`;
          return (
            <Cell key={code} subtitle={cap} after={info.available ? "Свободно" : "Занято"}>
              {meta.emoji} {meta.title}
            </Cell>
          );
        })}
      </Section>

      <Section header="Уровень и модели — скоро">
        <Cell subtitle="Появится с RCF-019 (профиль): 2 опытных · 2 средних · 1 начинающий" multiline>
          🎯 Уровень пилотов
        </Cell>
        <Cell subtitle="Появится с RCF-018 (гараж): 3 самолёта · 2 коптера" multiline>
          🛩 Состав авиапарка
        </Cell>
      </Section>

      <Section>
        <Cell>
          <Caption level="1">
            Имена и позывные показываются только тем пилотам, которые сами разрешат это в своём профиле.
            Без согласия — только числа.
          </Caption>
        </Cell>
        <Cell onClick={() => navigate(-1)}>← Назад</Cell>
      </Section>
    </List>
  );
}

function airspaceLine(day) {
  switch (day.airspace_status) {
    case "closed": return `❌ ${day.airspace_note || "Поле закрыто"}`;
    case "restricted": return `⚠️ ${day.airspace_note || "Есть ограничения"}`;
    default: return "✅ Открыто";
  }
}

function weatherLine(day) {
  if (!day.weather) return "Погоду подключим позже (Yandex Weather)";
  const { icon, temp_c, wind_ms } = day.weather;
  const parts = [];
  if (icon) parts.push(icon);
  if (temp_c != null) parts.push(`${temp_c}°`);
  if (wind_ms != null) parts.push(`ветер ${wind_ms} м/с`);
  return parts.join(" · ") || "—";
}

function pluralRu(n, one, few, many) {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

function endHhmm(iso) {
  const m = /T(\d{2}:\d{2})/.exec(iso || "");
  return m ? m[1] : "—";
}

function formatDateRu(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  return `${RU_WEEKDAYS[utc.getUTCDay()]} ${d} ${RU_MONTHS[m - 1]}`;
}
